import type { NotificationView, WorkspaceMembership } from '@app/shared';

/**
 * Pure, framework-free helpers for the application shell. Kept out of the React shell
 * component so they run in the cheap `node` test project and stay independently
 * verifiable (workspace label, notification routing).
 */

/** The five global-navigation sections, in order. `label`/`to` mirror the prior shell so
 * links keep the SAME accessible name (E2E specs select `getByRole('link', { name })`). */
export const NAV_SECTIONS = [
  { key: 'dashboard', label: 'Home', to: '/app/dashboard' },
  { key: 'agents', label: 'Agents', to: '/app/agents' },
  { key: 'runs', label: 'Runs', to: '/app/runs' },
  { key: 'approvals', label: 'Approvals', to: '/app/approvals' },
  { key: 'connections', label: 'Connections', to: '/app/connections' },
  { key: 'admin', label: 'Admin', to: '/app/admin' },
] as const;

/**
 * The app-shell workspace label. Renders the workspace's human NAME, never the raw hex
 * id (a truncated UUID reads as noise, not a name). Falls back to a bare "Workspace"
 * when the active membership has no usable name, and to '' (hidden) when there is no
 * workspace at all.
 */
export function workspaceLabel(workspaces: WorkspaceMembership[]): string {
  const active = workspaces[0];
  if (!active) return '';
  const name = active.name?.trim();
  return name ? name : 'Workspace';
}

/** Where a notification navigates + how it reads in the bell menu (#157). Falls back
 * to the approvals inbox for any unrecognised kind so a new server-side kind is never
 * a dead item. */
export function notificationTarget(n: NotificationView): {
  to: string;
  params?: Record<string, string>;
  primary: string;
  secondary: string;
} {
  const p = (n.payload ?? {}) as Record<string, unknown>;
  const str = (v: unknown) => (typeof v === 'string' ? v : undefined);
  if (n.kind === 'scheduled_run_finished') {
    const runId = str(p.runId);
    return {
      // #297: the run opens on its full detail page (the split-pane preview is removed).
      to: '/app/runs/$runId/full',
      params: runId ? { runId } : undefined,
      primary: `Scheduled run finished — ${str(p.agentName) ?? 'Agent'}`,
      secondary: `${str(p.status) ?? 'done'} · ${new Date(n.createdAt).toLocaleString()}`,
    };
  }
  // approval_requested / approval_decided / unknown → the approvals inbox.
  return {
    to: '/app/approvals',
    primary:
      n.kind === 'approval_decided'
        ? 'Approval decided'
        : `Approval needed: ${str(p.tool) ?? ''}`,
    secondary: new Date(n.createdAt).toLocaleString(),
  };
}
